import type { ActorView, ChannelView, PostView } from "../queries.ts";
import type { Post as PostRecord } from "../structs.ts";

// Read-your-own-write: the AppView usually hasn't ingested a post when the
// composer redirects to it, so freshly sent posts are stashed here and thread,
// quote, and context views fall back to them while the AppView 404s.
const storageKey = "cerulea.eagerPosts.v1";
const maxEntries = 20;
// Ingest lag is seconds; a day covers extended outages while bounding how long
// a stale copy (e.g. a post deleted from another client) can stand in.
export const eagerTtlMs = 24 * 60 * 60 * 1000;

export function buildEagerPost(input: {
  readonly uri: `at://${string}`;
  readonly cid: string;
  readonly author: ActorView;
  readonly record: PostRecord;
  readonly channel?: ChannelView;
  readonly replyTo?: ActorView;
}): PostView {
  return {
    uri: input.uri,
    cid: input.cid,
    author: input.author,
    record: input.record,
    ...(input.channel && { channel: input.channel }),
    ...(input.replyTo && { replyTo: input.replyTo }),
    replyCount: 0,
    likeCount: 0,
    repostCount: 0,
    quoteCount: 0,
  };
}

export function findEagerPost(
  posts: readonly PostView[],
  uri: string,
): PostView | undefined {
  const now = Date.now();
  return posts.find((post) => post.uri === uri && !isExpired(post, now));
}

export function upsertEagerPost(posts: readonly PostView[], post: PostView): PostView[] {
  const now = Date.now();
  return [post, ...posts.filter((entry) => entry.uri !== post.uri)]
    .filter((entry) => !isExpired(entry, now))
    .slice(0, maxEntries);
}

export function parseEagerPosts(value: unknown): PostView[] {
  if (!Array.isArray(value)) return [];
  return value.filter((entry): entry is PostView =>
    !!entry && typeof entry === "object" &&
    typeof (entry as PostView).uri === "string" &&
    typeof (entry as PostView).cid === "string" &&
    typeof (entry as PostView).author?.did === "string" &&
    typeof (entry as PostView).record?.text === "string"
  );
}

export function saveEagerPost(post: PostView): void {
  writeEagerPosts(upsertEagerPost(readEagerPosts(), post));
}

export function getEagerPost(uri: string): PostView | undefined {
  return findEagerPost(readEagerPosts(), uri);
}

export function removeEagerPost(uri: string): void {
  writeEagerPosts(readEagerPosts().filter((entry) => entry.uri !== uri));
}

function isExpired(post: PostView, now: number): boolean {
  const created = Date.parse(post.record.createdAt ?? "");
  return Number.isNaN(created) || now - created > eagerTtlMs;
}

function readEagerPosts(): PostView[] {
  try {
    if (typeof localStorage === "undefined") return [];
    return parseEagerPosts(JSON.parse(localStorage.getItem(storageKey) ?? "null"));
  } catch {
    return [];
  }
}

function writeEagerPosts(posts: readonly PostView[]): void {
  try {
    if (typeof localStorage === "undefined") return;
    if (!posts.length) localStorage.removeItem(storageKey);
    else localStorage.setItem(storageKey, JSON.stringify(posts));
  } catch {
    // Private mode and quotas: the cache simply does not persist.
  }
}
